import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { DayValue, Day } from 'react-modern-calendar-datepicker';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import moment from 'moment';

import { showNotification } from '@/utils/notificationUtils';

import './PetProfileForm.scss';

import { Loader } from '@/components/Loader/Loader';
import { Heading } from '@/components/Elements/Heading';
import { PetType } from '@/features/availability/components/PetType';
import { useGetPetSettings } from '../api/getPetSettings';
import { FileUploader } from './FileUploader';
import { Customers, PetSimple, Pets } from '..';
import { useUploadPicture } from '@/features/location/api/locationRequests';
import { useAddPet } from '@/features/events/api/addEmoji';
import PetWeightSelection from '@/features/availability/components/PetWeightSelection';
import { PetWeight } from '@/features/payment/api/petWeights';
import { DropDown } from '@/components/dropdown/DropDown';
import Utils from '@/utils/util';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Container } from '@/features/intakeform/components/DnDContainer';
import { FormField } from '@/features/intakeform/types';
import { useTranslation } from 'react-i18next';
import AssignedPetAttributeCard from '@/features/petAttributes/components/AssignedPetAttributeCard';
import { PetAttribute } from '@/features/petAttributes';
import DatePickerWithPortal from '@/components/DatePicker/DatepickerWithPortal';
type Props = {
  pet?: Pets;
  onChange?: (pet: Pets) => void;
  onSuccess: () => void;
  isEditMode: boolean;
  parent?: Customers;
  customFormFields?: FormField[];
};
export const PetProfileForm = ({
  pet,
  onChange,
  onSuccess,
  isEditMode,
  parent,
  customFormFields,
}: Props) => {
  const petSettingsQuery = useGetPetSettings();
  const { t, i18n } = useTranslation();
  const addPetMutation = useAddPet();
  const uploadPictureMutation = useUploadPicture();
  const [breedList, setBreedList] = useState<any[]>();
  const [colorList, setColorList] = useState<any[]>();
  const [data, setData] = useState<PetSimple>({
    name: '',
    breed: Utils.BREED_UNSPECIFIED,
    gender: '',
    weight: '',
    dateOfBirth: '',
    notes: '',
    petsImageUrl: '',
    id: '',
    petType: '',
    petColor: '',
    petSize: '',
    petAge: '',
  });

  useEffect(() => {
    pet &&
      setData({
        name: pet.name,
        breed: pet.breed,
        gender: pet.gender,
        weight: pet.weight,
        dateOfBirth: pet.dateOfBirth,
        notes: pet.notes,
        petsImageUrl: pet.petsImageUrl,
        id: pet.id,
        petType: pet.petType,
        petColor: pet.petColor,
        petSize: pet.petSize,
        petAge: pet.petAge,
      });
  }, [pet]);
  useEffect(() => {
    if (petSettingsQuery.data) {
      setBreedList(petSettingsQuery?.data?.breed_list.map((t: string) => ({ value: t, label: t })));
      setColorList(
        petSettingsQuery?.data?.pet_color_list.map((t: string) => ({ value: t, label: t }))
      );
    }
  }, [petSettingsQuery.data]);

  const [day, setDay] = React.useState<Day>({
    day: moment().date(),
    month: moment().month() + 1,
    year: moment().year(),
  } as Day);

  const onFormValueChange = (formFieldName: string, value: string) => {
    data[formFieldName as keyof typeof data] = value as any;
    setData({ ...data });
    onChange && onChange({ ...data });
  };

  const validate = () => {
    let errorMessage = undefined;
    const { name, petType, breed, dateOfBirth, gender, weight, petColor } = data;
    if (!name || name.trim().length < 1) {
      errorMessage = t('Please enter pet name');
    } else if (!breed || breed.trim().length < 1) {
      errorMessage = t('Please select the pet breed');
    } else if (!weight || weight.trim().length < 1) {
      errorMessage = t('Please enter pet weight');
      // } else if (!dateOfBirth || dateOfBirth.trim().length < 1) {
      //   errorMessage = 'Please select pet date of birth';
      // }
    } else if (!gender || gender.trim().length < 1) {
      errorMessage = t('Please select gender of the pet');
    }
    // else if (!petType || petType.trim().length < 1) {
    //   errorMessage = 'Please select pet type';
    // }
    else if (!petColor || petColor.trim().length < 1) {
      errorMessage = t('Please select color of the pet');
    }

    if (errorMessage) {
      showNotification({
        type: 'info',
        title: errorMessage,
      });
      return false;
    } else {
      return true;
    }
  };

  const onAddPetToServer = async () => {
    if (validate() && !addPetMutation.isLoading) {
      const {
        name,
        petType,
        breed,
        dateOfBirth,
        gender,
        weight,
        petColor,
        notes,
        petsImageUrl,
        id,
        petAge,
      } = data;

      await addPetMutation.mutateAsync({
        data: {
          id: id ? parseInt(id) : undefined,
          name,
          dateOfBirth,
          weight,
          breed,
          gender,
          notes,
          petsImageUrl,
          petType,
          petColor,
          petParentId: parent && parent.id,
          petAge,
        },
      });
      onSuccess();
    }
  };

  const onProfilePicUpload = async (formData: FormData) => {
    const response = await uploadPictureMutation.mutateAsync({
      file: formData,
    });
    //setImageUrl(response ? response.imageUrl : '');
    onFormValueChange('petsImageUrl', response ? response.imageUrl : '');
  };

  const onPetTypeChange = (petType: string) => {
    onFormValueChange('petType', petType);
  };
  const onPetSelect = (pet: Pets) => {
    setData(pet);
  };

  const onPetSizeChange = (weight: PetWeight) => {
    setData({ ...data, petSize: weight.key ? weight.key : '' });
  };
  return (
    <div className="pet-profile-form tab-content">
      {isEditMode && (
        <Heading
          title={t('Add New Pet')}
          description={t('Add details of your new pet below')}
          size="lg"
          alignText="left"
        />
      )}
      <div className="scroller">
        {isEditMode && (
          <FileUploader
            key={'pet-profile-pic'}
            onUpload={onProfilePicUpload}
            url={data.petsImageUrl}
            isUploading={uploadPictureMutation.isLoading}
          />
        )}
        <div className="petsked-form">
          <div className="form-field">
            <label className="form-label">{t('Select your pet type')}</label>
            <PetType readOnly={!isEditMode} onChange={onPetTypeChange} value={data.petType} />
          </div>
          <div className="form-field name">
            <label className="form-label">{t('Enter name and breed of your pet')}</label>
            <div className="split full pet">
              <input
                type="text"
                placeholder={t('Pet Name')}
                className="pet-name"
                value={data.name}
                disabled={!isEditMode}
                onChange={(e: any) => onFormValueChange('name', e.target.value)}
              />
              <DropDown
                placeholder={t('Select Breed')}
                selectedValue={data?.breed}
                data={breedList ? breedList : []}
                onSelect={(value: any) => {
                  onFormValueChange('breed', value);
                }}
                onDismiss={() => {}}
                enableSearch={true}
                isDisabled={!isEditMode}
              />
            </div>
          </div>
          <div className="form-field">
            <label className="form-label">{t('Select your pet size')}</label>
            <div className="selected-pet-row">
              {data && (
                <PetWeightSelection
                  readOnly={isEditMode ? false : true}
                  selectedSize={data.petSize}
                  onChange={onPetSizeChange}
                />
              )}
            </div>
          </div>
          {pet?.petAttributes && pet?.petAttributes.length > 0 && (
            <div className="form-field">
              <label className="form-label">{t('Pet Attributes')}</label>
              <div className="selected-pet-row">
                {pet.petAttributes.map((pa: PetAttribute) => {
                  return (
                    <AssignedPetAttributeCard
                      key={pa.id}
                      data={pa}
                      onDelete={() => {}}
                      isEditMode={false}
                    />
                  );
                })}
              </div>
            </div>
          )}
          <div className="form-field">
            <label className="form-label">{t('Enter some extra details below')}</label>
            <div className="split full pet">
              <DatePickerWithPortal
                value={data?.dateOfBirth}
                maximumDate={day}
                onChange={(val: DayValue) => {
                  onFormValueChange('dateOfBirth', `${val?.month}/${val?.day}/${val?.year}`);
                }}
                placeholder={t('Select date of birth')}
              />

              <DropDown
                placeholder={t('Select Color')}
                selectedValue={data?.petColor}
                data={colorList ? colorList : []}
                onSelect={(value: any) => {
                  onFormValueChange('petColor', value);
                }}
                onDismiss={() => {}}
                enableSearch={true}
                isDisabled={!isEditMode}
              />
            </div>
            <div className="split full">
              <span className="pet-weight-container">
                <input
                  type="text"
                  placeholder={t('Enter Weight')}
                  value={data.weight}
                  onChange={(e: any) => onFormValueChange('weight', e.target.value)}
                  disabled={!isEditMode}
                />
                <span>{t('lbs')}</span>
              </span>
              <div className="sex">
                <Form.Check
                  inline
                  value={'male'}
                  checked={data?.gender?.toLowerCase() === 'male' ? true : false}
                  label={t('Male')}
                  name="group1"
                  type="radio"
                  disabled={!isEditMode}
                  onChange={(e: any) => onFormValueChange('gender', e.target.value)}
                />
                <span className="seperator"></span>
                <Form.Check
                  inline
                  value={'female'}
                  checked={data?.gender?.toLowerCase() === 'female' ? true : false}
                  label={t('Female')}
                  name="group1"
                  type="radio"
                  disabled={!isEditMode}
                  onChange={(e: any) => onFormValueChange('gender', e.target.value)}
                />
              </div>
            </div>
            <div className="split full">
              <input
                type="text"
                placeholder={t('Enter age')}
                value={data.petAge}
                onChange={(e: any) => onFormValueChange('petAge', e.target.value)}
                disabled={!isEditMode}
                maxLength={25}
              />
              <div></div>
            </div>
            <textarea
              rows={4}
              value={data.notes}
              placeholder={t('Add note')}
              onChange={(e: any) => onFormValueChange('notes', e.target.value)}
              disabled={!isEditMode}
            />
          </div>
          {customFormFields && customFormFields.length > 0 && (
            <DndProvider backend={HTML5Backend}>
              <div className="custom-fields">
                <hr />
                <label>Intake Form Custom Fields:</label>
                <Container
                  disabled={true}
                  formFields={customFormFields}
                  onChange={(fFields) => {}}
                  onFieldValueChange={() => {}}
                  disableDrag={true}
                  preview={true}
                  key={`sffg.key`}
                />
              </div>
            </DndProvider>
          )}

          {isEditMode && (
            <div className="action">
              <button className="petsked-button button-dark" onClick={onAddPetToServer}>
                {addPetMutation.isLoading ? <Loader /> : <span>{t('Add New Pet')}</span>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
